import {ISSUE_TYPE_MILESTONE} from "../../constants";
import {Entity} from "../../domain/entities/entity";
import {Issue} from "../../domain/entities/issue.entity";
import {Project} from "../../domain/entities/project.entity";
import {belongsToRoot} from "../../domain/helpers/issue-type-root.helper";
import {BaseMessage} from "../base-message.interface";
import {EntityDeletedPayload} from "../payloads/entity-deleted.payload";

/**
 * Whether an entity.deleted message is about a milestone: a deleted issue on the Milestone root or on
 * one of its children (behaviour follows the root, D3). Its project's milestones are then reloaded. The
 * payload carries only the deleted entity (command-send.listener.ts publishes `{entity}`), so the issue
 * is recognised by its class, as filterOnIssueEntityFn in entity-deleted.listener.ts does.
 */
export function isMilestoneDeletion(message: BaseMessage<EntityDeletedPayload<Entity>>): boolean {
    const entity = message.payload.entity;
    // noinspection SuspiciousTypeOfGuard
    return entity instanceof Issue && belongsToRoot(entity.type, ISSUE_TYPE_MILESTONE);
}

/**
 * The projects whose milestones are reloaded after milestones were deleted: each deleted milestone's
 * project once, and only while it is still loaded — the same guard the create side
 * (entity-created.listener.ts onMilestoneCreated) applies. A milestone without a project is skipped.
 */
export function projectsToRefresh(milestones: readonly Issue[], isLoaded: (uid: string) => boolean): Project[] {
    const projects = new Map<string, Project>();
    for (const milestone of milestones) {
        const project = milestone.project;
        if (project && isLoaded(project.uid)) {
            projects.set(project.uid, project);
        }
    }
    return [...projects.values()];
}
